import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { apiClient } from '../../../services/api/client';
import { locationApi } from '../../../services/api';
import { useTenantContext } from '../../../contexts/TenantContext';

const InviteUserModal = ({ onClose, onSuccess, roleOptions, isGlobalAdmin, tenantId }) => {
    const { tenants } = useTenantContext();
    const [formData, setFormData] = useState({ 
        email: '', 
        firstName: '', 
        lastName: '', 
        role: 'User', 
        locationId: '',
        employeeId: '',
        tenantId: tenantId ?? '',
  });
    const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [employeesLoading, setEmployeesLoading] = useState(false);
    const [troncMasterExists, setTroncMasterExists] = useState(false);
    const [checkingTroncMaster, setCheckingTroncMaster] = useState(true);

  // Location is required for Manager and User roles
    const showLocationField = formData.role === 'User' || formData.role === 'Manager';
    const locationRequired = showLocationField;

    // Employee selector: required for User role, optional for Manager role
    const showEmployeeField = (formData.role === 'User' || formData.role === 'Manager') && formData.locationId;
    const employeeRequired = formData.role === 'User' && formData.locationId;

    // GlobalAdmin is a tenant-less role — no location needed
    const isGlobalAdminRole = formData.role === 'GlobalAdmin';

    useEffect(() => {
        const fetchLocations = async () => {
    try {
            setLocationsLoading(true);
       const response = await locationApi.getDropdown();
          const data = response?.data ?? response?.Data ?? [];
  const success = response?.success ?? response?.Success ?? false;
     if (success && data) {
        setLocations(data);
    }
            } catch (err) {
       console.error('Failed to fetch locations:', err);
            } finally {
  setLocationsLoading(false);
    }
        };
        fetchLocations();
  }, []);

    // Fetch employees when location is selected
    useEffect(() => {
        const fetchEmployees = async () => {
            if (!formData.locationId || !showEmployeeField) {
      setEmployees([]);
    return;
     }

 try {
         setEmployeesLoading(true);
   const response = await apiClient.get(`/employees?locationId=${formData.locationId}&activeOnly=true`);
  const data = response?.data ?? response?.Data ?? [];
       if (data) {
             setEmployees(data);
    }
  } catch (err) {
            console.error('Failed to fetch employees:', err);
                setEmployees([]);
            } finally {
             setEmployeesLoading(false);
  }
        };
    fetchEmployees();
    }, [formData.locationId, showEmployeeField]);

    // Check if a TroncMaster already exists (active user or pending invitation)
    useEffect(() => {
        const checkTroncMasterExists = async () => {
   try {
     setCheckingTroncMaster(true);
  // Check active users for TroncMaster role
      const usersResponse = await apiClient.get('/users');
   const users = usersResponse?.data ?? usersResponse?.Data ?? [];
     const hasTroncMasterUser = users.some(u => u.role === 'TroncMaster' && u.isActive);

        if (hasTroncMasterUser) {
           setTroncMasterExists(true);
      } else {
    // Also check pending invitations for TroncMaster role
   const invitationsResponse = await apiClient.get('/users/invitations');
     const invitations = invitationsResponse?.data ?? invitationsResponse?.Data ?? [];
       const hasTroncMasterInvite = invitations.some(
  i => i.role === 'TroncMaster' && i.status === 'Pending'
     );
         setTroncMasterExists(hasTroncMasterInvite);
         }
 } catch (err) {
   console.error('Failed to check TroncMaster status:', err);
                // Don't block the modal, just disable the option to be safe
         setTroncMasterExists(false);
            } finally {
setCheckingTroncMaster(false);
       }
        };
        checkTroncMasterExists();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            // Clear locationId and employeeId when switching to an admin role
       if (name === 'role' && value !== 'User' && value !== 'Manager') {
  updated.locationId = '';
       updated.employeeId = '';
         }
            // Clear employeeId when location changes
            if (name === 'locationId') {
                updated.employeeId = '';
       }
        return updated;
        });
    };

const handleSubmit = async (e) => {
    e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            const payload = { ...formData };
            // Only send locationId if one is selected and role requires it
    if (!payload.locationId || !showLocationField) {
  delete payload.locationId;
    }
    // Only send employeeId if one is selected
            if (!payload.employeeId) {
 delete payload.employeeId;
   }
            // Attach the selected tenant context for GlobalAdmin inviting tenant-scoped roles
            const effectiveTenantId = formData.tenantId || tenantId || null;
            if (effectiveTenantId && payload.role !== 'GlobalAdmin') {
                payload.tenantId = effectiveTenantId;
            }
   const response = await apiClient.post('/users/invite', payload);
       if (response.success) { onSuccess(); }
   else { setError(response.message || 'Failed to send invitation'); }
        } catch (err) { setError(err.message || 'Failed to send invitation'); }
        finally { setLoading(false); }
    };

    // GlobalAdmin must have a tenant selected when inviting tenant-scoped roles
    const needsTenantForRole = isGlobalAdmin && !isGlobalAdminRole && !formData.tenantId && !tenantId;

    const isValid = formData.email && formData.firstName && formData.lastName && 
  (!locationRequired || formData.locationId) &&
     (!employeeRequired || formData.employeeId) &&
     !needsTenantForRole;

    // Build role options with TroncMaster and optionally GlobalAdmin
    const allRoleOptions = [
        ...roleOptions,
        { value: 'TroncMaster', label: 'TRONC Master', description: 'Independent TRONC distribution manager (HMRC compliant)' },
    ...(isGlobalAdmin ? [{ value: 'GlobalAdmin', label: 'Global Admin', description: 'Full system access across all tenants' }] : []),
    ];

    // Check if the selected role is TroncMaster and it's blocked
    const isTroncMasterBlocked = formData.role === 'TroncMaster' && troncMasterExists;

    return (
<div className='fixed inset-0 z-[1200] flex items-center justify-center'>
   <div className='absolute inset-0 bg-black/50' onClick={onClose} />
 <div className='relative bg-card rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto'>
            <div className='flex items-center justify-between p-4 border-b border-border'>
  <h2 className='text-lg font-semibold'>Invite User</h2>
          <button onClick={onClose} className='p-2 hover:bg-muted rounded-md'><Icon name='X' size={20} /></button>
  </div>
           <form onSubmit={handleSubmit} className='p-4 space-y-4'>
  {error && (<div className='alert alert-error'><Icon name='AlertCircle' size={16} /><span>{error}</span></div>)}
 {needsTenantForRole && (
    <div className='alert alert-warning flex items-start gap-2'>
      <Icon name='AlertTriangle' size={16} className='shrink-0 mt-0.5' />
      <span>No tenant selected. Please select a tenant below or choose one from the sidebar.</span>
    </div>
  )}

  {/* Tenant selector for GlobalAdmin when no tenant pre-selected and role is not GlobalAdmin */}
  {isGlobalAdmin && !isGlobalAdminRole && !tenantId && (
    <div>
      <label className='block text-sm font-medium mb-1'>Tenant <span className='text-error'>*</span></label>
      <select
        name='tenantId'
        value={formData.tenantId}
        onChange={handleChange}
        className='select w-full'
        required
      >
        <option value=''>Select a tenant...</option>
        {(tenants || []).map(t => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>
      <p className='text-xs text-muted-foreground mt-1'>The tenant this user will belong to.</p>
    </div>
  )}
  {isGlobalAdmin && !isGlobalAdminRole && tenantId && (
    <div className='p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground'>
      <Icon name='Building2' size={14} className='inline mr-1' />
      Inviting to: <strong className='text-foreground'>{(tenants || []).find(t => t.id === tenantId)?.name ?? tenantId}</strong>
    </div>
  )}
 
 <div><label className='block text-sm font-medium mb-1'>Email *</label><input type='email' name='email' value={formData.email} onChange={handleChange} className='input w-full' placeholder='user@example.com' required /></div>
            
   <div className='grid grid-cols-2 gap-4'>
              <div><label className='block text-sm font-medium mb-1'>First Name *</label><input type='text' name='firstName' value={formData.firstName} onChange={handleChange} className='input w-full' required /></div>
        <div><label className='block text-sm font-medium mb-1'>Last Name *</label><input type='text' name='lastName' value={formData.lastName} onChange={handleChange} className='input w-full' required /></div>
            </div>
         
  <div>
      <label className='block text-sm font-medium mb-1'>Role *</label>
          <select name='role' value={formData.role} onChange={handleChange} className='select w-full'>
    {allRoleOptions.map(o => (
  <option
  key={o.value}
    value={o.value}
       disabled={o.value === 'TroncMaster' && troncMasterExists}
       >
          {o.label}{o.value === 'TroncMaster' && troncMasterExists ? ' (already assigned)' : ''}
         </option>
         ))}
     </select>
   {formData.role === 'TroncMaster' && !checkingTroncMaster && !troncMasterExists && (
 <div className='mt-2 p-3 bg-primary/5 border border-primary/20 rounded-lg'>
                <p className='text-xs text-muted-foreground'>
         <Icon name='Shield' size={14} className='inline mr-1 text-primary' />
       The TRONC Master is independent of the employer. By accepting, they confirm they do not have hiring authority, are not a decision maker, and will act independently (HMRC requirement).
     </p>
        </div>
         )}
      {formData.role === 'TroncMaster' && troncMasterExists && (
         <div className='mt-2 p-3 bg-warning/10 border border-warning/30 rounded-lg'>
               <p className='text-xs text-warning'>
         <Icon name='AlertTriangle' size={14} className='inline mr-1' />
    A TRONC Master already exists or has a pending invitation for this tenant. Only one TRONC Master is allowed per tenant. Revoke the existing invitation or deactivate the current TRONC Master first.
  </p>
    </div>
 )}
       {isGlobalAdminRole && (
  <div className='mt-2 p-3 bg-error/5 border border-error/20 rounded-lg'>
           <p className='text-xs text-muted-foreground'>
       <Icon name='ShieldAlert' size={14} className='inline mr-1 text-error' />
  Global Admins have unrestricted access to <strong>all tenants</strong> and system settings. This role is not tied to any tenant. Only invite trusted system administrators.
     </p>
    </div>
    )}
           </div>
             
          {showLocationField && !isGlobalAdminRole && (
       <div>
   <label className='block text-sm font-medium mb-1'>
          Location {locationRequired && <span className='text-error'>*</span>}
        </label>
  <select 
         name='locationId' 
               value={formData.locationId} 
          onChange={handleChange} 
        className='select w-full' 
                disabled={locationsLoading}
  required={locationRequired}
    >
          <option value=''>
 {locationsLoading ? 'Loading locations...' : 'Select a location'}
           </option>
  {locations.map(loc => (
     <option key={loc.id} value={loc.id}>{loc.name}{loc.code ? ` (${loc.code})` : ''}</option>
        ))}
      </select>
    <p className='text-xs text-muted-foreground mt-1'>
 {formData.role === 'User' 
         ? 'Required for User role. The user will be granted access to this location.' 
        : 'Required for Manager role. The user will be granted access to this location.'}
          </p>
            </div>
   )}

      {showEmployeeField && (
                 <div>
        <label className='block text-sm font-medium mb-1'>
       Link to Employee {employeeRequired && <span className='text-error'>*</span>}
      </label>
     <select 
   name='employeeId' 
       value={formData.employeeId} 
  onChange={handleChange} 
    className='select w-full' 
          disabled={employeesLoading}
           required={employeeRequired}
        >
         <option value=''>
        {employeesLoading 
        ? 'Loading employees...' 
          : formData.role === 'User'
   ? 'Select an employee (required)'
   : 'Select an employee (optional)'}
              </option>
            {employees.map(emp => (
       <option key={emp.id} value={emp.id}>
    {emp.firstName} {emp.lastName} {emp.payrollId ? `(${emp.payrollId})` : ''}
        </option>
))}
       </select>
        <p className='text-xs text-muted-foreground mt-1'>
         {formData.role === 'User' 
          ? 'Required for User role. Links this user account to an employee record for TRONC and clock records.' 
    : 'Optional for Manager role. Links the manager to their employee record if applicable.'}
       </p>
       </div>
           )}
                    
       <div className='flex justify-end gap-3 pt-4 border-t border-border'>
           <button type='button' onClick={onClose} className='btn btn-outline' disabled={loading}>Cancel</button>
             <button type='submit' className='btn btn-primary' disabled={loading || !isValid || isTroncMasterBlocked}>
    {loading ? 'Sending...' : 'Send Invitation'}
           </button>
     </div>
            </form>
            </div>
        </div>
    );
};

export default InviteUserModal;

